import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAppBlocker } from "../../Helpers/Blocker.jsx";
import { MdEdit, MdDelete, MdCheck, MdClose } from "react-icons/md";
import {
  useCreateModuleMutation,
  useUpdateModuleMutation,
  useGetModuleByIdQuery,
  useGetQuestionLazyQuery,
} from "../../../../graphql/generated.tsx";
import { v4 as uuidv4 } from "uuid";
import Loader from "../../../../components/Loader";
import { toast } from "../../../../toast";
import NetworkIndicator from "../../../Practice/Components/NetworkIndicator.jsx";

/* ─── Confirm / Info Modal ─────────────────────────────────────────────────── */
const ConfirmModal = ({ isOpen, title = "Confirmation", message, confirmText = "Continue", cancelText = "Cancel", secondaryText, showCancel = true, onConfirm, onCancel, onSecondary, isProcessing = false }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(10,18,30,0.55)', backdropFilter: 'blur(4px)' }}>
      <div style={{
        background: '#fff',
        borderRadius: 16,
        boxShadow: '0 24px 64px rgba(10,18,30,0.18)',
        maxWidth: 620,
        width: 'calc(100% - 40px)',
        minHeight: 270,
        overflow: 'hidden',
      }}>
        <div style={{ height: 4, background: 'linear-gradient(90deg, #1a3c5e, #2e7db5, #6ab4e8)' }} />
        <div style={{ minHeight: 266, padding: '36px 36px 32px', display: 'flex', flexDirection: 'column' }}>
          <h3 style={{ margin: '0 0 10px', fontSize: 16, fontWeight: 700, color: '#0f2033', fontFamily: "'Poppins', sans-serif" }}>{title}</h3>
          <p style={{ margin: '0 0 24px', fontSize: 14, color: '#4a5568', lineHeight: 1.6, whiteSpace: 'pre-wrap', fontFamily: "'Poppins', sans-serif" }}>{message}</p>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 'auto', paddingTop: 28 }}>
            {showCancel && (
              <button
                style={{ padding: '9px 20px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', color: '#4a5568', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: "'Poppins', sans-serif" }}
                onClick={onCancel} disabled={isProcessing}
              >{cancelText}</button>
            )}
            {secondaryText && (
              <button
                style={{ padding: '9px 20px', borderRadius: 8, border: '1.5px solid #fecaca', background: '#fff', color: '#b91c1c', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: "'Poppins', sans-serif" }}
                onClick={onSecondary} disabled={isProcessing}
              >{secondaryText}</button>
            )}
            <button
              style={{ padding: '9px 22px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg, #1a3c5e, #2563a8)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: isProcessing ? 'not-allowed' : 'pointer', opacity: isProcessing ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: 8, fontFamily: "'Poppins', sans-serif" }}
              onClick={onConfirm} disabled={isProcessing}
            >
              {isProcessing ? (<><span style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />Processing...</>) : confirmText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Difficulty badge ───────────────────────────────────────────────────────── */
const DiffBadge = ({ diff }) => {
  const map = {
    EASY:   { bg: '#dcfce7', color: '#15803d' },
    MEDIUM: { bg: '#fef9c3', color: '#92400e' },
    HARD:   { bg: '#fee2e2', color: '#b91c1c' },
  };
  const s = map[diff];
  if (!diff || !s) return null;
  return (
    <span style={{ padding: '2px 10px', borderRadius: 20, background: s.bg, color: s.color, fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', fontFamily: "'Poppins', sans-serif" }}>
      {diff}
    </span>
  );
};

/* ════════════════════════════════════════════════════════════════════════════
   Main Module Component
   ════════════════════════════════════════════════════════════════════════════ */
const Module = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const draftRestoredRef = useRef(false);
  const initialModuleRef = useRef({ name: "", description: "", domain: "" });

  const queryParams = new URLSearchParams(location.search);
  const moduleId = queryParams.get('id');
  const isEditing = location.state?.isEditing || !!moduleId;
  const isCreateMode = !moduleId;

  /* ── Core form state ────────────────────────────────────────────────────── */
  const [clusterName, setClusterName] = useState("");
  const [description, setDescription] = useState("");
  const [domain, setDomain] = useState("");
  const [clusterCreated, setClusterCreated] = useState(false);
  const [isInfoEditing, setIsInfoEditing] = useState(!moduleId);
  /* ── Day / question state ───────────────────────────────────────────────── */
  const [days, setDays] = useState([{ title: "", description: "", questions: [], saved: false }]);
  const [currentDayIndex, setCurrentDayIndex] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);
  const [dayDraft, setDayDraft] = useState({ title: "", description: "" });
  const [isDayEditing, setIsDayEditing] = useState(false);
  const cancelEditBtnRef = useRef(null);

  /* ── Modal state ────────────────────────────────────────────────────────── */
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [modalTitle, setModalTitle] = useState("Confirmation");
  const [modalConfirmText, setModalConfirmText] = useState("Continue");
  const [modalCancelText, setModalCancelText] = useState("Cancel");
  const [modalShowCancel, setModalShowCancel] = useState(true);
  const [modalProcessing, setModalProcessing] = useState(false);
  const modalActionRef = useRef(null);

  /* ── GraphQL hooks (UNCHANGED) ──────────────────────────────────────────── */
  const { data: moduleData, loading: moduleLoading } = useGetModuleByIdQuery({
    variables: { id: moduleId },
    skip: !moduleId,
    fetchPolicy: 'network-only',
    onError: (error) => { console.error("Error fetching module:", error); }
  });

  const [getQuestion] = useGetQuestionLazyQuery();
  const [createModuleMutation, { loading: createModuleLoading }] = useCreateModuleMutation();
  const [updateModuleMutation, { loading: updateModuleLoading }] = useUpdateModuleMutation();

  useEffect(() => {
    const rawDraft = sessionStorage.getItem("moduleDraft");
    const returningFromQuestionImport = Boolean(sessionStorage.getItem("moduleImportQuestions"));
    if (!rawDraft || !returningFromQuestionImport) return;
    try {
      const draft = JSON.parse(rawDraft);
      const draftModuleId = draft?.moduleId || null;
      if ((draftModuleId || null) !== (moduleId || null)) return;
      if (typeof draft?.clusterName === "string") setClusterName(draft.clusterName);
      if (typeof draft?.description === "string") setDescription(draft.description);
      if (typeof draft?.domain === "string") setDomain(draft.domain);
      if (Array.isArray(draft?.days)) setDays(draft.days);
      if (Number.isInteger(draft?.currentDayIndex)) setCurrentDayIndex(draft.currentDayIndex);
      setClusterCreated(true);
      draftRestoredRef.current = true;
    } catch { }
  }, [moduleId]);

  useEffect(() => { setIsInfoEditing(!moduleId); }, [moduleId]);

  useEffect(() => {
    const cur = days[currentDayIndex] || {};
    setDayDraft({ title: cur.title || "", description: cur.description || "" });
    setIsDayEditing(!isCreateMode ? false : true);
  }, [currentDayIndex, days, isCreateMode]);

  useEffect(() => {
    const raw = sessionStorage.getItem("moduleImportQuestions");
    if (!raw) return;
    try {
      const payload = JSON.parse(raw);
      const dayIndex = Number.isInteger(payload?.dayIndex) ? payload.dayIndex : currentDayIndex;
      const questions = Array.isArray(payload?.questions) ? payload.questions : [];
      if (questions.length === 0) return;
      setDays(prev => {
        const next = [...prev];
        const idx = Math.min(Math.max(dayIndex, 0), next.length - 1);
        const cur = next[idx];
        const merged = [...cur.questions];
        questions.forEach(q => { if (!merged.some(existing => existing.id === q.id)) merged.push(q); });
        next[idx] = { ...cur, questions: merged, saved: false };
        return next;
      });
      if (Number.isInteger(dayIndex)) setCurrentDayIndex(dayIndex);
      toast.success("Questions imported.", { position: "bottom-right", autoClose: 2000 });
    } catch { }
    finally { sessionStorage.removeItem("moduleImportQuestions"); }
  }, []);

  /* ── Modal helpers ──────────────────────────────────────────────────────── */
  const openInfoModal = (message, title = "Info") => {
    setModalTitle(title); setModalMessage(message); setModalConfirmText("OK"); setModalShowCancel(false);
    modalActionRef.current = () => setModalOpen(false);
    setModalOpen(true);
  };
  const openConfirmModal = ({ title = "Confirm", message, confirmText = "Continue", cancelText = "Cancel", action }) => {
    setModalTitle(title); setModalMessage(message); setModalConfirmText(confirmText); setModalCancelText(cancelText); setModalShowCancel(true);
    modalActionRef.current = action || (() => setModalOpen(false));
    setModalOpen(true);
  };

  /* ── Fetch question details ──────────────────────────────────────────────── */
  const fetchQuestionDetails = async (questionId) => {
    try {
      const result = await getQuestion({ variables: { id: questionId }, fetchPolicy: 'network-only' });
      return result.data?.getQuestion;
    } catch { return null; }
  };

  const fetchDayQuestionDetails = async (questions) => {
    setIsLoadingQuestions(true);
    const details = [];
    try {
      const batchSize = 5;
      for (let i = 0; i < questions.length; i += batchSize) {
        const results = await Promise.all(questions.slice(i, i + batchSize).map(q => fetchQuestionDetails(q.id)));
        details.push(...results.filter(Boolean));
      }
      return details;
    } catch { return []; } finally { setIsLoadingQuestions(false); }
  };

  /* ── Populate form from API ─────────────────────────────────────────────── */
  useEffect(() => {
    if (moduleData?.getModuleById) {
      const module = moduleData.getModuleById;
      initialModuleRef.current = { name: module.name || '', description: module.short_description || '', domain: module.domain || '' };
      if (draftRestoredRef.current) return;
      setClusterName(module.name || '');
      setDescription(module.short_description || '');
      setDomain(module.domain || '');
      setClusterCreated(true);
      if (module.daywise_plan?.length > 0) {
        const daysData = module.daywise_plan.map(day => ({
          day_id: day.day_id,
          title: day.name || day.title || "",
          description: day.day_description || day.description || "",
          questions: (Array.isArray(day.questions) ? day.questions : []).map(question => ({
            id: typeof question === "string" ? question : question.id,
            ...(typeof question === "object" ? question : {}),
          })),
          saved: true
        }));
        setDays(daysData);
        const firstDay = daysData[0];
        if (firstDay) {
          setDayDraft({ title: firstDay.title, description: firstDay.description });
        }
        (async () => {
          for (let i = 0; i < daysData.length; i++) {
            const questionDetails = await fetchDayQuestionDetails(daysData[i].questions);
            if (questionDetails.length > 0) {
              setDays(prev => {
                const updated = [...prev];
                updated[i] = {
                  ...updated[i],
                  questions: questionDetails.map(q => ({
                    id: q.id, title: q.title, description: q.question_sub_text || '',
                    difficulty: q.difficulty, tags: q.tags || [],
                    type: q.category?.name || '', topic: q.company_tags?.[0] || ''
                  }))
                };
                return updated;
              });
            }
          }
        })();
      }
    }
  }, [moduleData]);

  /* ── Unsaved navigation guard ─────────────────────────────────────────── */
  const hasUnsavedProgress = !isSaving && (
    (!clusterCreated && [clusterName, description, domain].some(value => value.trim())) ||
    (clusterCreated && (
      days.some(d => !d.saved) ||
      (days[currentDayIndex] && (dayDraft.title !== (days[currentDayIndex].title || "") || dayDraft.description !== (days[currentDayIndex].description || ""))) ||
      clusterName !== initialModuleRef.current.name ||
      description !== initialModuleRef.current.description ||
      domain !== initialModuleRef.current.domain
    ))
  );

  useEffect(() => {
    if (!hasUnsavedProgress) return;
    const handler = e => { e.preventDefault(); e.returnValue = "Unsaved changes."; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasUnsavedProgress]);

  /* Blocks in-app navigation AND browser back/swipe (POP) when there are unsaved changes. */
  const blocker = useAppBlocker(
    hasUnsavedProgress,
    React.useCallback((pathname) => pathname.includes("/admin/module/import-questions"), [])
  );

  /* ── Payload builder ────────────────────────────────────────────────────── */
  function getModuleMutationPayload(sourceDays = days) {
    const startDate = new Date(); startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(startDate); endDate.setDate(endDate.getDate() + (sourceDays.length - 1)); endDate.setHours(23, 59, 59, 999);
    return {
      name: clusterName, short_description: description, domain,
      start_date: startDate.toISOString(), end_date: endDate.toISOString(),
      daywise_plan: sourceDays.map((day, idx) => {
        const dayDate = new Date(startDate); dayDate.setDate(dayDate.getDate() + idx);
        return {
          day_id: day.day_id || uuidv4(),
          name: day.title?.trim() ? day.title : `Day ${idx + 1} Assignment`,
          day_description: day.description || "",
          questions: day.questions.map(q => q.id),
          createdAt: dayDate.toISOString(),
        };
      })
    };
  }

  /* ── Handlers ────────────────────────────────────────────────────────────── */
  const handleCreateClick = () => {
    if (clusterName.trim() && description.trim() && domain.trim()) {
      setClusterCreated(true);
    } else {
      openInfoModal("Please fill in all fields", "Missing fields");
    }
  };

  const confirmAndDeleteQuestion = (questionId) => {
    openConfirmModal({
      title: "Remove question", message: "Remove this question from the current day?",
      confirmText: "Remove", cancelText: "Cancel",
      action: () => setDays(prev => {
        const n = [...prev];
        n[currentDayIndex] = { ...n[currentDayIndex], questions: n[currentDayIndex].questions.filter(q => q.id !== questionId) };
        return n;
      })
    });
  };

  const confirmAndDeleteDay = (dayIndex) => {
    if (days.length <= 1) {
      openInfoModal("At least one day is required.", "Cannot remove day");
      return;
    }
    openConfirmModal({
      title: "Remove day",
      message: `Remove Day ${dayIndex + 1}? All questions in this day will also be removed.`,
      confirmText: "Remove",
      cancelText: "Cancel",
      action: () => {
        setDays((prev) => prev.filter((_, idx) => idx !== dayIndex));
        setCurrentDayIndex((prev) => {
          if (prev === dayIndex) return Math.max(0, dayIndex - 1);
          if (prev > dayIndex) return prev - 1;
          return prev;
        });
      },
    });
  };

  const handleSaveCluster = async () => {
    if (!clusterName.trim()) { openInfoModal("Please enter a cluster name", "Missing cluster name"); return; }
    const committedDays = days.map((day, idx) => (
      idx === currentDayIndex
        ? { ...day, title: dayDraft.title, description: dayDraft.description }
        : day
    ));
    const emptyDays = days.map((d, i) => ({ i, c: d.questions.length })).filter(d => d.c === 0);
    if (emptyDays.length > 0) {
      openInfoModal(`Day(s) with no questions: ${emptyDays.map(d => `Day ${d.i + 1}`).join(", ")}`, "Empty day(s)");
      return;
    }
    setIsSaving(true);
    const payload = getModuleMutationPayload(committedDays);
    try {
      const token = localStorage.getItem("idtoken");
      const uid = JSON.parse(localStorage.getItem("user"))?.uid;
      if (!token || !uid) throw new Error("Authentication required.");
      let result;
      if (isEditing && moduleId) {
        result = await updateModuleMutation({ variables: { id: moduleId, data: payload }, context: { headers: { authorization: token, clientid: uid } }, errorPolicy: "all" });
        toast.success("Task updated successfully!", { position: "top-right", autoClose: 3000 });
      } else {
        result = await createModuleMutation({ variables: { data: payload }, context: { headers: { authorization: token, clientid: uid } }, errorPolicy: "all" });
        toast.success("Task created successfully!", { position: "top-right", autoClose: 3000 });
      }
      setDays(prev => prev.map((d, idx) => idx === currentDayIndex ? { ...d, title: dayDraft.title, description: dayDraft.description, saved: true } : { ...d, saved: true }));
      initialModuleRef.current = { name: clusterName, description, domain };
      sessionStorage.removeItem("moduleDraft");
      navigate("/admin/practice-lab");
    } catch (err) {
      openInfoModal(`Failed to ${isEditing ? "update" : "create"} task. ${err.message || ""}`, "Save failed");
    } finally { setIsSaving(false); }
  };

  const updateDayAtIndex = (index, patch) => {
    setDays(prev => {
      const n = [...prev];
      const next = { ...n[index], ...patch };
      if (!Object.prototype.hasOwnProperty.call(patch, "saved")) next.saved = false;
      n[index] = next;
      return n;
    });
  };

  const commitDayDraft = () => {
    updateDayAtIndex(currentDayIndex, { title: dayDraft.title, description: dayDraft.description });
  };

  const handleDayFieldBlur = (e) => {
    if (cancelEditBtnRef.current && cancelEditBtnRef.current === e.relatedTarget) return;
    commitDayDraft();
  };

  const saveModuleDraft = () => {
    const draftDays = days.map((day, index) => (
      index === currentDayIndex
        ? { ...day, title: dayDraft.title, description: dayDraft.description }
        : day
    ));
    sessionStorage.setItem("moduleDraft", JSON.stringify({
      moduleId: moduleId || null,
      clusterName,
      description,
      domain,
      days: draftDays,
      currentDayIndex,
    }));
  };

  const discardModuleDraftAndLeave = () => {
    sessionStorage.removeItem("moduleDraft");
    blocker.proceed();
  };

  /* ══════════════════════════════════════════════════════════════════════════
     COMPUTED
     ══════════════════════════════════════════════════════════════════════════ */
  const currentDay = days[currentDayIndex];
  const totalDays = days.length;
  const totalQuestions = days.reduce((sum, day) => sum + (day.questions?.length || 0), 0);
  const currentDayQuestionCount = currentDay?.questions?.length || 0;
  const isBusy = isSaving || createModuleLoading || updateModuleLoading;

  /* ══════════════════════════════════════════════════════════════════════════
     STYLES
     ══════════════════════════════════════════════════════════════════════════ */
  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap');
    @keyframes spin { to { transform: rotate(360deg); } }
    @keyframes fadeSlideIn { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }
    @keyframes pulse-dot { 0%,100% { opacity:1; } 50% { opacity:0.3; } }
    .mod-root * { box-sizing: border-box; }
    .mod-root { font-family: 'Poppins', sans-serif; background: #fff; min-height: 100vh; }
    .day-btn-active { background: linear-gradient(135deg, #1a3c5e 0%, #1e5080 100%) !important; color: #fff !important; box-shadow: 0 4px 14px rgba(26,60,94,0.28) !important; }
    .day-btn { background: #fff; border: 1.5px solid #e8ecf2; color: #334155; transition: all 0.15s ease; }
    .day-btn:hover { border-color: #93c5fd; background: #f0f7ff; }
    .day-item-wrap { position: relative; }
    .day-delete-btn {
      position: absolute;
      top: 8px;
      right: 8px;
      width: 24px;
      height: 24px;
      border-radius: 7px;
      border: 1px solid #fecaca;
      background: #fff;
      color: #dc2626;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      opacity: 0;
      pointer-events: none;
      transition: all 0.15s ease;
    }
    .day-item-wrap:hover .day-delete-btn { opacity: 1; pointer-events: auto; }
    .day-delete-btn:hover { background: #fef2f2; }
    .q-card { animation: fadeSlideIn 0.2s ease both; }
    .q-card:hover { border-color: #bfdbfe !important; background: #f8fbff !important; }
    .field-input { width:100%; padding: 10px 14px; border: 1.5px solid #e2e8f0; border-radius: 10px; font-size: 14px; font-family: 'Poppins',sans-serif; color: #1e293b; background: #fff; outline:none; transition: border-color 0.15s, box-shadow 0.15s; }
    .field-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59,130,246,0.12); }
    .field-label { display:block; font-size:11px; font-weight:700; letter-spacing:0.07em; color:#94a3b8; text-transform:uppercase; margin-bottom:6px; }
    .btn-primary { display:inline-flex; align-items:center; gap:8px; padding: 10px 22px; border-radius:10px; border:none; background: linear-gradient(135deg,#1a3c5e,#2563a8); color:#fff; font-size:13px; font-weight:700; font-family:'Poppins',sans-serif; cursor:pointer; transition:opacity 0.15s, transform 0.1s; letter-spacing:0.02em; }
    .btn-primary:hover:not(:disabled) { opacity:0.92; transform:translateY(-1px); }
    .btn-primary:disabled { opacity:0.6; cursor:not-allowed; }
    .btn-outline { display:inline-flex; align-items:center; gap:6px; padding:8px 18px; border-radius:9px; border:1.5px solid #1a3c5e; background:#fff; color:#1a3c5e; font-size:12px; font-weight:700; font-family:'Poppins',sans-serif; cursor:pointer; transition:all 0.15s; letter-spacing:0.02em; }
    .btn-outline:hover { background:#f0f7ff; }
    .stat-chip { display:inline-flex; align-items:center; gap:5px; padding:5px 12px; border-radius:20px; font-size:12px; font-weight:600; }
    .tag-chip { padding:2px 9px; border-radius:12px; font-size:11px; font-weight:600; background:#eff6ff; color:#1d4ed8; }
    .type-chip { padding:2px 9px; border-radius:12px; font-size:11px; font-weight:600; background:#fff7ed; color:#c2410c; }
  `;

  /* ══════════════════════════════════════════════════════════════════════════
     RENDER
     ══════════════════════════════════════════════════════════════════════════ */
  return (
    <>
      <style>{css}</style>
      <NetworkIndicator />
      <div className="mod-root">

        {/* ── TOP HEADER BAR ─────────────────────────────────────────────── */}
        <div style={{ background: '#fff', borderBottom: '1px solid #e8ecf2', padding: '0 32px', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 40 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#0f2033', fontFamily: "'Poppins', sans-serif", lineHeight: 1.1 }}>
                {moduleId ? "Edit Task" : "Create Task"}
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500, marginTop: 1 }}>
                {moduleId ? "Update existing learning task" : "Build a new learning task"}
              </div>
            </div>
          </div>
          {clusterCreated && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <span className="stat-chip" style={{ background: '#f0f9ff', color: '#0369a1' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  {totalDays} {totalDays === 1 ? 'Day' : 'Days'}
                </span>
                <span className="stat-chip" style={{ background: '#f0fdf4', color: '#166534' }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  {totalQuestions} Questions
                </span>
              </div>
              <button className="btn-primary" onClick={handleSaveCluster} disabled={isBusy} style={{ padding: '9px 20px' }}>
                {isBusy ? (
                  <><span style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.35)', borderTopColor: '#fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />Saving…</>
                ) : (
                  <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>{moduleId ? "Update Task" : "Save Task"}</>
                )}
              </button>
            </div>
          )}
        </div>

        {/* ── LOADING ─────────────────────────────────────────────────────── */}
        {moduleLoading && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 0', gap: 14 }}>
            <div style={{ width: 40, height: 40, border: '3px solid #e2e8f0', borderTopColor: '#2563a8', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
            <p style={{ fontSize: 13, color: '#94a3b8', fontFamily: "'Poppins', sans-serif" }}>Loading task…</p>
          </div>
        )}

        {!moduleLoading && (
          <div style={{ maxWidth: 1280, margin: '0 auto', padding: '28px 32px 40px' }}>

            {/* ── MODULE INFO CARD ──────────────────────────────────────── */}
            <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e8ecf2', boxShadow: '0 2px 12px rgba(10,18,30,0.05)', marginBottom: 28, overflow: 'hidden' }}>
              <div style={{ height: 3, background: 'linear-gradient(90deg, #1a3c5e 0%, #2e7db5 50%, #7ec8ea 100%)' }} />
              <div style={{ padding: '20px 24px 22px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#1a3c5e', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Task Details</div>
                    {clusterCreated && !isInfoEditing && (
                      <span style={{ padding: '2px 10px', borderRadius: 20, background: '#dcfce7', color: '#166534', fontSize: 11, fontWeight: 700 }}>Saved</span>
                    )}
                  </div>
                  {isEditing && clusterCreated && (
                    <button
                      type="button"
                      onClick={() => setIsInfoEditing(prev => !prev)}
                      style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: isInfoEditing ? '#f0f7ff' : '#fff', color: '#334155', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: "'Poppins', sans-serif" }}
                    >
                      {isInfoEditing ? (
                        <MdCheck style={{ width: 13, height: 13 }} />
                      ) : (
                        <MdEdit style={{ width: 13, height: 13 }} />
                      )}
                      {isInfoEditing ? "Done" : "Edit"}
                    </button>
                  )}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: 20 }}>
                  {/* Module Name */}
                  <div>
                    <label className="field-label">Task Name</label>
                    {isInfoEditing ? (
                      <input className="field-input" type="text" value={clusterName} onChange={e => setClusterName(e.target.value)} placeholder="e.g. DSA Foundations" />
                    ) : (
                      <div style={{ fontSize: 15, fontWeight: 700, color: '#0f2033', padding: '4px 0' }}>{clusterName || <span style={{ color: '#cbd5e1' }}>—</span>}</div>
                    )}
                  </div>
                  {/* Domain */}
                  <div>
                    <label className="field-label">Domain</label>
                    {isInfoEditing ? (
                      <input className="field-input" type="text" value={domain} onChange={e => setDomain(e.target.value)} placeholder="e.g. Data Structures" />
                    ) : (
                      <div style={{ fontSize: 15, fontWeight: 700, color: '#0f2033', padding: '4px 0' }}>{domain || <span style={{ color: '#cbd5e1' }}>—</span>}</div>
                    )}
                  </div>
                  {/* Description */}
                  <div>
                    <label className="field-label">Description</label>
                    {isInfoEditing ? (
                      <textarea className="field-input" value={description} onChange={e => setDescription(e.target.value)} placeholder="Short description of this task…" rows={2} style={{ resize: 'none' }} />
                    ) : (
                      <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.65, padding: '4px 0', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {description || <span style={{ color: '#cbd5e1' }}>—</span>}
                      </div>
                    )}
                  </div>
                </div>

                {!clusterCreated && (
                  <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end' }}>
                    <button className="btn-primary" type="button" onClick={handleCreateClick}>
                      Continue to Day Editor
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* ── DAY EDITOR ───────────────────────────────────────────── */}
            {clusterCreated && (
              <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: 20, alignItems: 'start' }}>

                {/* ── LEFT SIDEBAR ──────────────────────────────────────── */}
                <div style={{ position: 'sticky', top: 72 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 10 }}>Days</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {days.map((day, idx) => {
                      const isActive = idx === currentDayIndex;
                      const displayName = day.title?.trim() ? day.title : `Day ${idx + 1}`;
                      const qCount = day.questions?.length || 0;
                      return (
                        <React.Fragment key={idx}>
                          <div className="day-item-wrap">
                            <button
                              type="button"
                              onClick={() => setCurrentDayIndex(idx)}
                              className={isActive ? 'day-btn-active' : 'day-btn'}
                              style={{ width: '100%', textAlign: 'left', borderRadius: 10, padding: '10px 13px', cursor: 'pointer', border: isActive ? 'none' : undefined }}
                            >
                              <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', opacity: isActive ? 0.7 : 0.55, marginBottom: 2 }}>
                                Day {idx + 1}
                              </div>
                              <div style={{ fontSize: 13, fontWeight: 700, color: isActive ? '#fff' : '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }}>
                                {displayName}
                              </div>
                              <div style={{ fontSize: 11, marginTop: 4, color: isActive ? 'rgba(255,255,255,0.65)' : '#94a3b8', fontWeight: 500 }}>
                                {qCount} question{qCount !== 1 ? 's' : ''}
                              </div>
                            </button>
                            {days.length > 1 && (
                              <button
                                type="button"
                                className="day-delete-btn"
                                onClick={(e) => { e.stopPropagation(); confirmAndDeleteDay(idx); }}
                                title="Delete day"
                                aria-label={`Delete day ${idx + 1}`}
                              >
                                <MdDelete style={{ width: 13, height: 13 }} />
                              </button>
                            )}
                          </div>
                          {idx === days.length - 1 && (
                            <button
                              onClick={() => {
                                setDays(prev => [...prev, { title: "", description: "", questions: [], saved: false }]);
                                setCurrentDayIndex(days.length);
                              }}
                              style={{ width: '100%', padding: '9px 13px', borderRadius: 10, border: '2px dashed #86efac', background: '#f0fdf4', color: '#15803d', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 4, fontFamily: "'Poppins', sans-serif", transition: 'all 0.15s' }}
                            >
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                              Add Day
                            </button>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>

                {/* ── CENTER PANEL ──────────────────────────────────────── */}
                <div>
                  {/* Day Info Card */}
                  <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e8ecf2', boxShadow: '0 2px 10px rgba(10,18,30,0.05)', marginBottom: 16, overflow: 'hidden' }}>
                    <div style={{ padding: '16px 22px', borderBottom: '1px solid #f1f5f9', background: 'linear-gradient(90deg,#f8fafc,#fff)' }}>
                        {isCreateMode || isDayEditing ? (
                        <div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 16, alignItems: 'start' }}>
                            <div>
                              <label className="field-label">Day Title</label>
                              <input
                                className="field-input"
                                type="text"
                                value={dayDraft.title}
                                onChange={e => setDayDraft(prev => ({ ...prev, title: e.target.value }))}
                                onBlur={handleDayFieldBlur}
                                onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); }}
                                placeholder={`Title for Day ${currentDayIndex + 1}`}
                              />
                            </div>
                            <div>
                              <label className="field-label">Day Description</label>
                              <textarea
                                className="field-input"
                                value={dayDraft.description}
                                onChange={e => setDayDraft(prev => ({ ...prev, description: e.target.value }))}
                                onBlur={handleDayFieldBlur}
                                placeholder={`Description for Day ${currentDayIndex + 1}`}
                                rows={4}
                                style={{ resize: 'vertical' }}
                              />
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, marginTop: 14 }}>
                            <button
                              type="button"
                              onClick={() => {
                                saveModuleDraft();
                                const allExistingQuestionIds = days.flatMap((day) =>
                                  (day.questions || []).map((question) => question.id)
                                );
                                navigate(`/admin/module/import-questions?day=${currentDayIndex}`, {
                                  state: {
                                    existingQuestionIds: allExistingQuestionIds,
                                  },
                                });
                              }}
                              className="btn-outline"
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                              Import Questions
                            </button>
                            {!isCreateMode && (
                              <>
                                <button
                                  ref={cancelEditBtnRef}
                                  type="button"
                                  onClick={() => { const cur = days[currentDayIndex] || {}; setDayDraft({ title: cur.title || "", description: cur.description || "" }); setIsDayEditing(false); }}
                                  style={{ padding: '9px 14px', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', color: '#475569', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontFamily: "'Poppins', sans-serif" }}
                                >
                                  <MdClose style={{ width: 14, height: 14 }} /> Cancel
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 2 }}>Day {currentDayIndex + 1}</div>
                              <div style={{ fontSize: 16, fontWeight: 800, color: '#0f2033', fontFamily: "'Poppins', sans-serif" }}>
                                {dayDraft.title?.trim() || `Untitled Day ${currentDayIndex + 1}`}
                              </div>
                            </div>
                            {dayDraft.description?.trim() && (
                              <div style={{ borderLeft: '2px solid #e2e8f0', paddingLeft: 18 }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 2 }}>Description</div>
                                <div style={{ fontSize: 13, color: '#475569', maxWidth: 380, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dayDraft.description}</div>
                              </div>
                            )}
                            <div style={{ borderLeft: '2px solid #e2e8f0', paddingLeft: 18 }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 2 }}>Questions</div>
                              <div style={{ fontSize: 15, fontWeight: 800, color: '#1a3c5e' }}>{currentDayQuestionCount}</div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <button
                              type="button"
                              onClick={() => {
                                saveModuleDraft();
                                const allExistingQuestionIds = days.flatMap((day) =>
                                  (day.questions || []).map((question) => question.id)
                                );
                                navigate(`/admin/module/import-questions?day=${currentDayIndex}`, {
                                  state: {
                                    existingQuestionIds: allExistingQuestionIds,
                                  },
                                });
                              }}
                              className="btn-outline"
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                              Import Questions
                            </button>
                            {!isCreateMode && (
                              <button
                                type="button"
                                onClick={() => setIsDayEditing(true)}
                                style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', color: '#475569', cursor: 'pointer', transition: 'all 0.15s' }}
                                title="Edit day"
                              >
                                <MdEdit style={{ width: 14, height: 14 }} />
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Questions List */}
                    <div style={{ padding: '16px 22px', minHeight: 240 }}>
                      {isLoadingQuestions && (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 0', gap: 12 }}>
                          <div style={{ width: 28, height: 28, border: '2.5px solid #e2e8f0', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                          <span style={{ fontSize: 13, color: '#94a3b8' }}>Loading questions…</span>
                        </div>
                      )}

                      {!isLoadingQuestions && currentDay?.questions?.length === 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '52px 0', gap: 12, textAlign: 'center' }}>
                          <div style={{ width: 56, height: 56, borderRadius: 16, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/></svg>
                          </div>
                          <div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: '#64748b', marginBottom: 4 }}>No questions yet</div>
                            <div style={{ fontSize: 12, color: '#94a3b8' }}>Click "Import Questions" above to add questions to this day</div>
                          </div>
                        </div>
                      )}

                      {!isLoadingQuestions && currentDay?.questions?.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          {currentDay.questions.map((question, qi) => (
                            <div
                              key={question.id}
                              className="q-card"
                              style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', border: '1.5px solid #e8ecf2', borderRadius: 12, background: '#fff', transition: 'all 0.15s', animationDelay: `${qi * 0.03}s` }}
                            >
                              <div style={{ width: 28, height: 28, flexShrink: 0, borderRadius: 8, background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: '#64748b' }}>
                                {qi + 1}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 14, fontWeight: 600, color: '#0f2033', marginBottom: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {question.title}
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 6 }}>
                                  <DiffBadge diff={question.difficulty} />
                                  {question.type && <span className="type-chip">{question.type}</span>}
                                </div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                                  {question.tags?.map((tag, i) => (
                                    <span key={i} className="tag-chip">{tag}</span>
                                  ))}
                                </div>
                              </div>
                              <button
                                onClick={() => confirmAndDeleteQuestion(question.id)}
                                style={{ width: 32, height: 32, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, border: '1.5px solid #fee2e2', background: '#fff', color: '#dc2626', cursor: 'pointer', transition: 'all 0.15s' }}
                                title="Remove question"
                              >
                                <MdDelete style={{ width: 15, height: 15 }} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Modal ─────────────────────────────────────────────────────────── */}
      <ConfirmModal
        isOpen={modalOpen} title={modalTitle} message={modalMessage}
        confirmText={modalConfirmText} cancelText={modalCancelText}
        showCancel={modalShowCancel} isProcessing={modalProcessing}
        onCancel={() => { setModalOpen(false); modalActionRef.current = null; }}
        onConfirm={async () => {
          try {
            setModalProcessing(true);
            const action = modalActionRef.current;
            if (action) { const r = action(); if (r?.then) await r; }
          } catch (err) { console.error("Modal action error:", err); }
          finally { setModalProcessing(false); setModalOpen(false); modalActionRef.current = null; }
        }}
      />

      {/* ── Unsaved changes blocker modal (in-app nav + browser back/swipe) ── */}
      {blocker.state === "blocked" && (
        <ConfirmModal
          isOpen
          title="Save before leaving?"
          message="This module has unsaved changes. Save the module before leaving, or leave without saving."
          confirmText="Save & Exit"
          cancelText="Cancel"
          secondaryText="Leave Without Saving"
          onCancel={() => blocker.reset()}
          onSecondary={discardModuleDraftAndLeave}
          onConfirm={handleSaveCluster}
        />
      )}
    </>
  );
};

export default Module;